import { create } from 'zustand';

import { UsersState, UserStore } from './types';
import api from '@/services/api';
import { ErrorResponse } from '@/utils';
import { DEFAULT_USERS_PER_PAGE } from '@/constants/env';
import { SearchParams } from '@/types';

const initialState: UsersState = {
  users: null,
  pagination: null,
  filters: {},
  loading: false,
  fetchError: null,
  createError: null,
};

const useUsersStore = create<UserStore>((set, getState) => ({
  ...initialState,

  fetchUsers: async (params: SearchParams = {}) => {
    const state = getState();
    set({ loading: true });
    // Aktiven Such- und Sortier-State beibehalten, damit ein Sort-Wechsel die
    // Suche nicht verwirft und umgekehrt. params duerfen beides ueberschreiben.
    const merged: SearchParams = {
      per_page: DEFAULT_USERS_PER_PAGE,
      page: 1,
      search: state.search,
      sort: state.sort,
      // Aktive Filter mitschicken, damit sie bei Sort/Refetch erhalten
      // bleiben. params duerfen weiterhin alles ueberschreiben.
      ...state.filters,
      ...params,
    };
    try {
      const { data, meta } = await api.user.getUsers(merged);
      set({ users: data, pagination: meta, sort: merged.sort });
    } catch (error) {
      set({ fetchError: (error as ErrorResponse).getType() });
    } finally {
      set({ loading: false });
    }
  },

  nextPage: async () => {
    const state = getState();
    if (!state.users || !state.pagination) return;
    if (state.pagination.current_page === state.pagination.last_page) return;

    set({ loading: true });
    try {
      const { data, meta } = await api.user.getUsers({
        per_page: state.pagination.per_page || DEFAULT_USERS_PER_PAGE,
        page: state.pagination.current_page + 1,
        search: state.search,
        sort: state.sort,
        ...state.filters,
      });
      set({ users: data, pagination: meta });
    } catch (error) {
      set({ fetchError: (error as ErrorResponse).getType() });
    } finally {
      set({ loading: false });
    }
  },

  prevPage: async () => {
    const state = getState();
    if (!state.users || !state.pagination) return;
    if (state.pagination.current_page === 1) return;

    set({ loading: true });
    try {
      const { data, meta } = await api.user.getUsers({
        per_page: state.pagination.per_page || DEFAULT_USERS_PER_PAGE,
        page: state.pagination.current_page - 1,
        search: state.search,
        sort: state.sort,
        ...state.filters,
      });
      set({ users: data, pagination: meta });
    } catch (error) {
      set({ fetchError: (error as ErrorResponse).getType() });
    } finally {
      set({ loading: false });
    }
  },

  newUser: async (user) => {
    const state = getState();
    set({ loading: true, createError: null });
    try {
      await api.user.createUser({
        ...user,
        password_confirmation: user.password,
      });
      await state.fetchUsers();
    } catch (e) {
      const error = (e as ErrorResponse).getResponseMessage();
      set({ createError: error });
      throw new Error(error);
    } finally {
      set({ loading: false });
    }
  },

  searchUsers: async (value: string) => {
    const state = getState();
    set({ loading: true });
    try {
      const { data, meta } = await api.user.getUsers({
        per_page: state.pagination?.per_page || DEFAULT_USERS_PER_PAGE,
        page: 1,
        search: value,
        sort: state.sort,
        ...state.filters,
      });
      set({ users: data, pagination: meta, search: value });
    } catch (error) {
      set({ fetchError: (error as ErrorResponse).getType() });
    } finally {
      set({ loading: false });
    }
  },

  // Filter setzen und ab Seite 1 neu laden. Such- und Sortier-State bleiben
  // erhalten (fetchUsers merged sie aus dem Store).
  setFilters: async (filters) => {
    set({ filters });
    await getState().fetchUsers({ page: 1 });
  },

  clearCreateErrors: () => {
    set({ createError: null });
  },

  updateUser: async (userId, userData) => {
    const state = getState();
    set({ loading: true, createError: null });
    try {
      await api.user.updateUser(userId, userData);
      await state.fetchUsers();
    } catch (e) {
      const error = (e as ErrorResponse).getResponseMessage();
      set({ createError: error });
      throw new Error(error);
    } finally {
      set({ loading: false });
    }
  },

  deleteUser: async (userId: number) => {
    const state = getState();
    set({ loading: true });
    try {
      await api.user.deleteUser(userId);
      await state.fetchUsers();
    } catch (error) {
      set({ fetchError: (error as ErrorResponse).getType() });
      // Weiterreichen: die Zeile zeigt die Backend-Meldung (z. B. 422
      // „Benutzer erst deaktivieren oder Kunden umverteilen", Sentry J/7R).
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  toggleUserStatus: async (userId: number, isActive: boolean) => {
    const state = getState();
    set({ loading: true });
    try {
      await api.user.toggleUserStatus(userId, isActive);
      await state.fetchUsers();
    } catch (error) {
      set({ fetchError: (error as ErrorResponse).getType() });
    } finally {
      set({ loading: false });
    }
  },

  clearState: () => {
    set(initialState);
  },
}));

export default useUsersStore;
